//#region src/live.ts
function ShakaLiveMixin(BaseClass) {
	class ShakaLive extends BaseClass {
		#targetLiveWindow = NaN;
		#liveEdgeStartOffset;
		#seekToLiveAbort = null;
		#seekToLivePending = false;
		constructor(...args) {
			super(...args);
			const { engine } = this;
			if (!engine) return;
			engine.addEventListener("loading", this.#onLoading);
			engine.addEventListener("unloading", this.#onUnloading);
			engine.addEventListener("manifestparsed", this.#derive);
			engine.addEventListener("manifestupdated", this.#derive);
			engine.addEventListener("loaded", this.#onLoaded);
		}
		attach(target) {
			super.attach(target);
			this.#armSeekToLive();
		}
		detach() {
			this.#disarmSeekToLive();
			super.detach();
		}
		destroy() {
			const { engine } = this;
			engine?.removeEventListener("loading", this.#onLoading);
			engine?.removeEventListener("unloading", this.#onUnloading);
			engine?.removeEventListener("manifestparsed", this.#derive);
			engine?.removeEventListener("manifestupdated", this.#derive);
			engine?.removeEventListener("loaded", this.#onLoaded);
			this.#disarmSeekToLive();
			this.#reset();
			super.destroy();
		}
		get targetLiveWindow() {
			return this.#targetLiveWindow;
		}
		get liveEdgeStart() {
			if (this.#liveEdgeStartOffset === void 0) return NaN;
			const { engine } = this;
			if (!engine) return NaN;
			const { end } = engine.seekRange();
			if (!Number.isFinite(end)) return NaN;
			return end - this.#liveEdgeStartOffset;
		}
		#onLoading = () => {
			this.#reset();
			if (!this.#seekToLivePending) this.#armSeekToLive();
		};
		#onUnloading = () => this.#reset();
		#onLoaded = () => {
			this.#derive();
			if (!this.#seekToLivePending) return;
			this.#seekToLivePending = false;
			this.#trySeekToLive();
		};
		#derive = () => {
			const { engine } = this;
			if (!engine || !(engine.isLive() || engine.isInProgress())) {
				this.#reset();
				return;
			}
			const { maxSegmentDuration } = engine.getStats();
			this.#liveEdgeStartOffset = Number.isFinite(maxSegmentDuration) && maxSegmentDuration > 0 ? maxSegmentDuration * 3 : void 0;
			this.#setTargetLiveWindow(engine.isInProgress() ? Number.POSITIVE_INFINITY : 0);
		};
		#reset() {
			this.#liveEdgeStartOffset = void 0;
			this.#setTargetLiveWindow(NaN);
		}
		#setTargetLiveWindow(value) {
			if (Object.is(this.#targetLiveWindow, value)) return;
			this.#targetLiveWindow = value;
			this.dispatchEvent(new Event("targetlivewindowchange"));
		}
		/**
		* Arm a one-shot seek-to-live on the first user-initiated `play`. Skipped when `autoplay` is set, since Shaka
		* positions at the live edge during its own startup sequence and a programmatic seek would race that.
		*/
		#armSeekToLive() {
			this.#disarmSeekToLive();
			const target = this.target;
			if (!target || target.autoplay) return;
			this.#seekToLiveAbort = new AbortController();
			target.addEventListener("play", () => {
				this.#seekToLivePending = true;
				this.#trySeekToLive();
			}, {
				signal: this.#seekToLiveAbort.signal,
				once: true
			});
		}
		#disarmSeekToLive() {
			this.#seekToLiveAbort?.abort();
			this.#seekToLiveAbort = null;
			this.#seekToLivePending = false;
		}
		#trySeekToLive() {
			const target = this.target;
			if (!target) return;
			const { liveEdgeStart } = this;
			if (!Number.isFinite(liveEdgeStart)) return;
			if (target.currentTime < liveEdgeStart) target.currentTime = liveEdgeStart;
			this.#seekToLivePending = false;
		}
	}
	return ShakaLive;
}
//#endregion
export { ShakaLiveMixin };
